import {
  Brand,
  BrandName,
  Domains,
  FlightrightBrandDomains,
  FlyhjaelpBrandDomains,
  BrandFamily,
  FlugrechtDomains,
  AllrightDomains,
  ChevalierDomains, FreemDomains, BlitzDomains,
} from "@/core/types/types";
import Script from "next/script";
import { GoogleTagManager } from "./GoogleTagManager/GoogleTagManager";
import getBrandConfiguration from "@/core/utils/getBrandConfiguration";

interface ScriptLoaderProps {
  brandName: Brand;
  tld: Domains;
}

interface HotjarProps {
  brandName: Brand;
}

interface ExtraGTMStatsScriptProps {
  brandName: Brand;
  tld: Domains;
}

const ScriptLoader = ({ brandName, tld }: ScriptLoaderProps) => {
  return (
    <>
      <TrustPilotScript />
      <CookieBot brandName={brandName} />
      <GoogleTagManager brandName={brandName} />
      <Hotjar brandName={brandName} />
      <ExtraGTMStatsScript brandName={brandName} tld={tld} />
    </>
  );
};

const TrustPilotScript = () => {
  return (
    <Script
      type="text/javascript"
      src="https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
      async
    />
  );
};

const CookieBot = ({ brandName }: BrandName) => {
  const cbid: string = getBrandConfiguration(brandName).cookieBotConfigs;
  const src = "https://consent.cookiebot.com/uc.js";
  return (
    <Script
      id="cookiebot"
      src={src}
      data-cbid={cbid}
      data-blockingmode="auto"
      type="text/javascript"
    />
  );
};

const Hotjar = ({ brandName }: HotjarProps) => {
  const allrightHotjarScript: string = `(function(h,o,t,j,a,r){ h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)}; h._hjSettings={hjid:5067992,hjsv:6}; a=o.getElementsByTagName('head')[0]; r=o.createElement('script');r.async=1; r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv; a.appendChild(r); })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`;

  const generalHotjarScript: string = `window.hj =
    window.hj ||
    function () {
      (hj.q = hj.q || []).push(arguments);
    };`;

  const isAllright: boolean = brandName === Brand.ALLRIGHT;

  const script: string = isAllright
    ? allrightHotjarScript
    : generalHotjarScript;

  return (
    <Script id="hotjar" strategy="beforeInteractive">
      {script}
    </Script>
  );
};

const ExtraGTMStatsScript = ({ brandName, tld }: ExtraGTMStatsScriptProps) => {
  let domain;

  const brandFamily = getBrandConfiguration(brandName)?.brandFamily;

  if (brandFamily === BrandFamily.FLUGRECHT) {
    domain = FlugrechtDomains[tld] || FlugrechtDomains[Domains.DE];
  } else if (brandFamily === BrandFamily.ALLRIGHT) {
    domain = AllrightDomains[tld] || AllrightDomains[Domains.DE];
  } else if (brandFamily === BrandFamily.FLIGHTRIGHT) {
    domain = FlightrightBrandDomains[tld];
  } else if (brandFamily === BrandFamily.FLYHJAELP) {
    domain = FlyhjaelpBrandDomains[tld];
  } else if (brandFamily === BrandFamily.CHEVALIER) {
    domain = ChevalierDomains[tld] || ChevalierDomains[Domains.DE];
  } else if (brandFamily === BrandFamily.FREEM) {
    domain = FreemDomains[tld] || FreemDomains[Domains.DE];
  } else if (brandFamily === BrandFamily.BLITZ) {
    domain = BlitzDomains[tld] || BlitzDomains[Domains.DE];
  }

  if (domain === undefined) {
    return null;
  }

  return (
    <Script id="flightright-dd360" strategy="afterInteractive">
      {`
        (function(w,d,s,l){
          w[l]=w[l]||[];
          w[l].push({'dd360.start': new Date().getTime(), event:'dd360.js'});
          var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),
          dl=l!='dataLayer'?'&l='+l:'';
          j.async=true;
          j.src='https://lynx.${domain}/lynx/client?id='+dl;
          f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer');
      `}
    </Script>
  );
};

export { ScriptLoader };
